import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "The trials and tribulations of a large-ish dataset",
  "description": "",
  "published": "2013-01-15T00:00:00.000Z",
  "redirect_from": ["/blog/the-trials-and-tribulations-of-a-large-ish-dataset/swizec/5852"],
  "categories": "Data, Data set, Database, Github, MongoDB, Programming, Redis, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Last week I wrote a little script in node.js.`}</p>
    <p>{`Its goal? GET ALL THE DATA!`}</p>
    <p><a parentName="p" {...{
        "href": "http://nightowls.swizec.com/histogram/hours"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2013/01/commit-histogram.png",
          "alt": "Commit time histogram",
          "title": "Commit time histogram"
        }}></img></a></p>
    <p>{`The plan was to scrape a massive `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Data_set",
        "title": "Data set"
      }}>{`dataset`}</a>{` off `}<a parentName="p" {...{
        "href": "http://github.com",
        "title": "GitHub"
      }}>{`Github`}</a>{` and do some analysis of programmers' working habits, at least the ones using Github that is. As such, the script was fairly simple - run a search query via the API, get page 1, 2, 3 ...`}</p>
    <p>{`At 100 repositories per page with a round-trip of a few seconds and having to wait for an hour before being allowed to do more requests after every couple of hundred pages, the scraping job took the bigger part of a week. Occasionally the script would hang without getting a `}<em parentName="p">{`403 error`}</em>{` so I changed the query. That fixed it.`}</p>
    <p>{`Sometimes it would crash - right after I'd gone to bed of course.`}</p>
    <p>{`On Saturday morning I had a mongo database with a list of `}<em parentName="p">{`513,900`}</em>{` repositories on a small EC2 instance. They were not guaranteed to be unique.`}</p>
    <h2 {...{
      "id": "when-n-becomes-a-problem"
    }}>{`When N becomes a problem`}</h2>
    <p>{`The next step was going through the list of repositories and fetching punchcard data for each.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2013/01/swizec-punchcard.png",
        "alt": "This is a punchcard",
        "title": "This is a punchcard"
      }}></img></p>
    <p>{`A core saying of practical computer science goes something like `}<em parentName="p">{`"Our O(N^5) algorithm sucks for large N, but N is usually less than 10"`}</em></p>
    <p>{`Although not a very big dataset by most means, at half a million, it was enough to thwart my original approach of using `}<a parentName="p" {...{
        "href": "http://mongoosejs.com/"
      }}>{`Mongoose`}</a>{` to handle my mongodb connection.`}</p>
    <p>{`While Mongoose was great for writing, it failed completely at reading. Not exactly certain where the problem was, but running this sort of code quickly made my EC2 instance run out of memory:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`models.repo.find({}, function (err, repos) {
  repos.forEach(do_something);
});
`}</code></pre>
    <p>{`Where `}<em parentName="p">{`models.repo`}</em>{` is the Schema for a repository.`}</p>
    <p>{`So I was forced to ditch the relative comforts afforded by Mongoose and read my data the old fashioned way:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`mongo.Db.connect(
        format("mongodb://%s:%s/github-nightowls?w=1", 'localhost', 27017),
        function(err, _db) {

            var repos = _db.collection('repos'),
                punchcards = _db.collection('punchcards');

            repos.find().each(function (err, repo) {
// ...
`}</code></pre>
    <p>{`A lot more work, but it doesn't even show up as a blip on memory usage. My guess is that Mongoose was doing the wrong thing and didn't stream data right out of the database into my `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Callback_%28computer_programming%29",
        "title": "Callback (computer programming)"
      }}>{`callback function`}</a>{`, but was trying to keep it all in memory. Silly Mongoose.`}</p>
    <p>{`There was one more step! Ensuring I only fetch punchcard data for unique repositories.`}</p>
    <p>{`My first attempt was going through the data, adding each repository's username/name combination to a redis set, then following that set when fetching the punchcards. Remember, sets guarantee each member only shows up once.`}</p>
    <p>{`Alas. After running a script for a few minutes - oh yes, did I mention it takes about 3 minutes just to read through the entire list of repositories? - it turns out the `}<a parentName="p" {...{
        "href": "http://redis.io/",
        "title": "Redis (data store)"
      }}>{`Redis`}</a>{` set was so massive my attempts at reading it were to no avail. Ran out of memory before even doing anything serious.`}</p>
    <p>{`This time I'm not sure it's `}<a parentName="p" {...{
        "href": "https://github.com/mranney/node_redis"
      }}>{`node_redis`}</a>{`'s fault or Redis really cannot stream data out of sets as is reading them.`}</p>
    <p>{`Eventually the algorithm to fetch punchards worked like this:`}</p>
    <ol>
      <li parentName="ol">{`Read object from Mongo`}</li>
      <li parentName="ol">{`Check Redis set if object already processed`}</li>
      <li parentName="ol">{`Fetch punchard`}</li>
      <li parentName="ol">{`Store punchcard`}</li>
      <li parentName="ol">{`Add new member to Redis set marking we've done this`}</li>
    </ol>
    <p>{`Because of some inconsistencies in Github responses the first time I ran this, it crashed 5 minutes after I had gone to sleep.`}</p>
    <p>{`Next run went much better!`}</p>
    <p>{`Except the instance ran out of disk space after processing some 110k repositories and I only noticed after waiting 14 hours for everything to process. Kids, print your errors!`}</p>
    <p>{`Third time is a charm and on Monday morning I had all the punchcards in a roughly 6 gigabyte mongo database. All `}<em parentName="p">{`504,015`}</em>{` of them.`}</p>
    <p>{`Luckily I had spent the previous day coding up a script calculating histograms - takes about `}<em parentName="p">{`3 minutes`}</em>{` to munch through the dataset - and playing with d3.js to make it look good.`}</p>
    <p>{`In all, the dataset contains `}<em parentName="p">{`164,509,270`}</em>{` commits. Only in punchcard form for now. This means I only have a 7x24 grid of buckets saying how many commits happened at that time.`}</p>
    <p>{`Next step - finding a way to distinguish hobby and work projects to see if that affects the distribution of commit times. Wish me luck.`}</p>
    <p>{`PS: If you want the dataset, send me an email and we can arrange something`}</p>
    <p>{`PPS: for those interested, all the code for fetching `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/github-nightowls"
      }}>{`my dataset is on github`}</a></p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/14164108/deploying-an-mvc4-c-sharp-application-to-azure-via-github-what-should-be-in-my"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_110_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/14164108/deploying-an-mvc4-c-sharp-application-to-azure-via-github-what-should-be-in-my"
        }}>{`Deploying an MVC4 C# application to Azure via GitHub. What should be in my .gitignore?`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://pypi.python.org/pypi/BigJob/0.4.122"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/138108576_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://pypi.python.org/pypi/BigJob/0.4.122"
        }}>{`BigJob 0.4.122`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/14262433/large-data-work-flows-using-pandas"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_96_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/14262433/large-data-work-flows-using-pandas"
        }}>{`"Large data" work flows using pandas`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://port3000.co.uk/imageoptimiser-post-mortem-of-a-github-bot"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/134901638_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://port3000.co.uk/imageoptimiser-post-mortem-of-a-github-bot"
        }}>{`imageoptimiser: Post mortem of a GitHub bot`}</a></li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=b124a913-ba09-45d1-84c3-ff52995f95e1",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      